import { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';
import { graphql, useMutation } from 'react-relay';

import { useAudioPlayerStore } from '../../audio-playback/hooks/useAudioPlayerStore';
import type { FlagsContainer } from '../../audio-playback/ui/audio-player/AudioContainer';

import type {
	ConfluenceUpdateAudioPreferenceInput,
	useUpdateAudioPreferencesMutation,
} from './__generated__/useUpdateAudioPreferencesMutation.graphql';

const messages = defineMessages({
	errorTitle: {
		id: 'ai-audio.set-audio-preferences.error',
		defaultMessage: 'Something went wrong',
		description: 'Error message shown when an error occurs.',
	},
	errorDescription: {
		id: 'ai-audio.set-audio-preferences.description',
		defaultMessage: 'Failed to update audio preferences',
		description: 'Description shown when settings were not updated',
	},
});

export const useUpdateAudioPreferences = (flags?: FlagsContainer) => {
	const { cloudId } = useAudioPlayerStore();
	const { formatMessage } = useIntl();

	const [updateAudioPreferencesMutation, isUpdateAudioPreferencesInFlight] =
		useMutation<useUpdateAudioPreferencesMutation>(graphql`
			mutation useUpdateAudioPreferencesMutation(
				$cloudId: ID!
				$input: ConfluenceUpdateAudioPreferenceInput!
			) {
				confluence_updateAudioPreference(cloudId: $cloudId, input: $input) {
					success
					errors {
						message
						extensions {
							statusCode
						}
					}
				}
			}
		`);

	const updateAudioPreferences = useCallback(
		(input: ConfluenceUpdateAudioPreferenceInput) => {
			return new Promise((resolve, reject) => {
				updateAudioPreferencesMutation({
					variables: {
						cloudId,
						input,
					},
					onCompleted: (response) => {
						const success = response.confluence_updateAudioPreference?.success;
						const errors = response.confluence_updateAudioPreference?.errors;
						if (success) {
							resolve(true);
						} else {
							const errorMessage = errors?.[0]?.message || 'Failed to update audio preferences';
							reject(new Error(errorMessage));
							if (flags) {
								flags.showFlag({
									title: formatMessage(messages.errorTitle),
									description: formatMessage(messages.errorDescription),
									type: 'error',
									close: 'auto',
								});
							}
						}
					},
					onError: (error) => {
						reject(error);

						if (flags) {
							flags.showFlag({
								title: formatMessage(messages.errorTitle),
								description: formatMessage(messages.errorDescription),
								type: 'error',
								close: 'auto',
							});
						}
					},
				});
			});
		},
		[updateAudioPreferencesMutation, cloudId, flags, formatMessage],
	);

	return {
		updateAudioPreferences,
		isUpdateAudioPreferencesInFlight,
	};
};
